/**
 * Content API client (Sprint 3 B5).
 *
 * Wrap 2 endpoint:
 *   - GET /api/content/lessons/<slug>/video/playback/ — signed HLS URL
 *   - POST /api/content/lessons/<slug>/video/upload/ — instructor init upload
 *
 * Em mirror response shape từ backend serializer (apps/content/serializers.py).
 */

import { useQuery } from "@tanstack/react-query";
import { apiClient } from "./client";

// ─── Types ────────────────────────────────────────────────────────────────

export type VideoProvider = "" | "mock" | "local" | "r2_hls" | "cloudflare";

export interface VideoPlaybackPayload {
  playback_url: string;
  expires_in_seconds: number;
  duration_seconds: number | null;
  thumbnail_url: string;
  provider: VideoProvider;
}

export interface VideoNotReadyError {
  detail: string;
  video_status: "pending" | "uploading" | "processing" | "ready" | "error";
}

/** Response body khi non-preview lesson + chưa enroll (Sprint 4 B3 gate). */
export interface NotEnrolledError {
  detail: string;
  code: "not_enrolled";
  course_slug: string;
}

// ─── Playback hook ────────────────────────────────────────────────────────

/**
 * Fetch signed playback URL cho lesson.
 *
 * Returns:
 *   - data: VideoPlaybackPayload nếu lesson có video READY
 *   - error: Axios error nếu 404 (no video) hoặc 409 (not ready) hoặc 401/403
 *
 * staleTime: 50 min — backend TTL 60 min, refetch trước expiry 10 min để
 * tránh stream đứt giữa video.
 */
export function useVideoPlayback(lessonSlug: string | undefined) {
  return useQuery({
    queryKey: ["content", "playback", lessonSlug],
    queryFn: async () => {
      const resp = await apiClient.get<VideoPlaybackPayload>(
        `/content/lessons/${lessonSlug}/video/playback/`,
      );
      return resp.data;
    },
    enabled: Boolean(lessonSlug),
    staleTime: 50 * 60 * 1000, // 50 min
    retry: (failureCount, error) => {
      // Không retry 4xx — chỉ retry network errors
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
  });
}

// ─── Tài liệu đính kèm bài học (2026-10-02) ───────────────────────────────
// PDF / PowerPoint / Word — metadata nhúng sẵn trong lesson detail
// (`attachments`), file tải qua apiClient (kèm JWT) dạng blob.

export interface LessonAttachment {
  id: number;
  original_filename: string;
  file_ext: string;
  size_bytes: number;
  created_at: string;
}

export const ATTACHMENT_ACCEPT = ".pdf,.ppt,.pptx,.doc,.docx";
export const ATTACHMENT_MAX_MB = 20;

export async function uploadLessonAttachment(
  lessonSlug: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<LessonAttachment> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await apiClient.post<LessonAttachment>(
    `/content/lessons/${lessonSlug}/attachments/`,
    formData,
    {
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    },
  );
  return res.data;
}

export async function deleteLessonAttachment(id: number): Promise<void> {
  await apiClient.delete(`/content/attachments/${id}/`);
}

export async function downloadLessonAttachment(attachment: LessonAttachment): Promise<void> {
  const res = await apiClient.get<Blob>(`/content/attachments/${attachment.id}/download/`, {
    responseType: "blob",
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = attachment.original_filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Bản PDF để xem ngay trên trang (PPT/Word đã được BE chuyển sang PDF). */
export async function fetchLessonAttachmentPreview(id: number): Promise<Blob> {
  const res = await apiClient.get<Blob>(`/content/attachments/${id}/preview/`, {
    responseType: "blob",
  });
  return res.data;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
